import React, { useState, useEffect, useCallback } from 'react';
import { Package, AlertTriangle, Search, Save, Plus, Minus } from 'lucide-react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';

// Stock de perfumes de 10ml (solo regalos de promoción, no se venden). Se edita con el valor
// EXACTO (no suma/resta) y se guarda al salir del campo.
const Mini10Cell = ({ product, fetchWithAuth, onSaved }) => {
  const current = product.stock_10ml || 0;
  const [value, setValue] = useState(String(current));
  useEffect(() => { setValue(String(current)); }, [current]);

  const save = async () => {
    const next = Math.max(parseInt(value, 10) || 0, 0);
    if (next === current) { setValue(String(current)); return; }
    try {
      const res = await fetchWithAuth('/api/admin/products/bulk-stock-update', {
        method: 'POST',
        body: { updates: [{ product_id: product.id, stock_10ml: next }] }
      });
      if (res.ok) { toast.success(`${product.product_code}: 10ml = ${next}`); onSaved(); }
      else { toast.error('No se pudo guardar'); setValue(String(current)); }
    } catch (e) { toast.error('Error al guardar'); setValue(String(current)); }
  };

  return (
    <input
      type="number"
      min="0"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      onBlur={save}
      onKeyDown={(e) => e.key === 'Enter' && e.currentTarget.blur()}
      className="w-16 bg-gray-700 border border-gray-600 rounded py-1 px-1 text-white text-center text-sm focus:outline-none focus:border-amber-500"
      data-testid={`stock-10ml-${product.product_code}`}
    />
  );
};

const AdminInventory = () => {
  const { fetchWithAuth } = useAdminAuth();
  const [products, setProducts] = useState([]);
  const [alerts, setAlerts] = useState({ low_stock: [], out_of_stock: [] });
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sortBy, setSortBy] = useState('product_code');
  const [sortOrder, setSortOrder] = useState('asc');
  // pendingChanges now handles both sizes: { productId: { stock_100: number, stock_40: number } }
  const [pendingChanges, setPendingChanges] = useState({});

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => { loadInventory(); loadAlerts(); }, [pagination.page, sortBy, sortOrder, debouncedSearch]);

  const loadInventory = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: pagination.page, limit: 100, sort_by: sortBy, sort_order: sortOrder });
      if (debouncedSearch) params.append('search', debouncedSearch);
      const res = await fetchWithAuth(`/api/admin/inventory?${params}`);
      if (res.ok) {
        const data = await res.json();
        setProducts(data.products);
        setSummary(data.summary);
        setPagination(prev => ({ ...prev, ...data.pagination }));
      }
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  };

  const loadAlerts = async () => {
    try {
      const res = await fetchWithAuth('/api/admin/inventory/alerts');
      if (res.ok) {
        const data = await res.json();
        setAlerts(data);
      }
    } catch (error) { console.error(error); }
  };

  // Handle stock change for a specific size (100 or 40)
  const handleStockChange = (productId, currentStock100, currentStock40, delta, sizeType) => {
    setPendingChanges(prev => {
      const currentChanges = prev[productId] || { stock_100: 0, stock_40: 0 };
      const fieldKey = sizeType === 100 ? 'stock_100' : 'stock_40';
      const currentStock = sizeType === 100 ? currentStock100 : currentStock40;
      
      const current = currentChanges[fieldKey] || 0;
      const newAdd = current + delta;
      
      // Don't allow negative final stock
      if (currentStock + newAdd < 0) {
        return prev;
      }
      
      const updatedChanges = { ...currentChanges, [fieldKey]: newAdd, currentStock100, currentStock40 };
      
      // If both changes are 0, remove the entry
      if (updatedChanges.stock_100 === 0 && updatedChanges.stock_40 === 0) {
        const { [productId]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [productId]: updatedChanges };
    });
  };

  const handleDirectInput = (productId, currentStock100, currentStock40, value, sizeType) => {
    const numValue = parseInt(value) || 0;
    setPendingChanges(prev => {
      const currentChanges = prev[productId] || { stock_100: 0, stock_40: 0 };
      const fieldKey = sizeType === 100 ? 'stock_100' : 'stock_40';
      
      const updatedChanges = { ...currentChanges, [fieldKey]: numValue, currentStock100, currentStock40 };
      
      // If both changes are 0, remove the entry
      if (updatedChanges.stock_100 === 0 && updatedChanges.stock_40 === 0) {
        const { [productId]: removed, ...rest } = prev;
        return rest;
      }
      
      return { ...prev, [productId]: updatedChanges };
    });
  };

  const savePendingChanges = async () => {
    // Calculate final stock values for both sizes
    const updates = Object.entries(pendingChanges).map(([product_id, change]) => ({ 
      product_id, 
      stock: change.currentStock100 + (change.stock_100 || 0),
      stock_40ml: change.currentStock40 + (change.stock_40 || 0)
    }));
    if (updates.length === 0) return;

    try {
      const res = await fetchWithAuth('/api/admin/products/bulk-stock-update', { method: 'POST', body: { updates } });
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.updated_count} productos actualizados`);
        setPendingChanges({});
        loadInventory();
        loadAlerts();
      }
    } catch (error) { toast.error('Error al guardar'); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 0 }).format(v);

  const getStockColor = (stock) => {
    if (stock === 0) return 'text-red-400 bg-red-500/10';
    if (stock <= 5) return 'text-amber-400 bg-amber-500/10';
    return 'text-green-400 bg-green-500/10';
  };

  // Calculate total units (100ml + 40ml)
  const getTotalStock = () => {
    return products.reduce((acc, p) => acc + (p.stock || 0) + (p.stock_40ml || 0), 0);
  };

  return (
    <div className="space-y-6" data-testid="admin-inventory">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold text-white">Inventario</h2>
          <p className="text-gray-400 text-sm">{summary.total_products || 0} productos • Gestión de stock por presentación</p>
        </div>
        {Object.keys(pendingChanges).length > 0 && (
          <button onClick={savePendingChanges} className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-black font-medium rounded-lg">
            <Save size={18} />Guardar ({Object.keys(pendingChanges).length} cambios)
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-3xl font-semibold text-white">{summary.total_stock || 0}</p>
          <p className="text-sm text-gray-400">Unidades 100ml</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-3xl font-semibold text-neon-cyan">{summary.total_stock_40ml || 0}</p>
          <p className="text-sm text-gray-400">Unidades 35ml</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-3xl font-semibold text-amber-400">{summary.low_stock_count || 0}</p>
          <p className="text-sm text-gray-400">Stock bajo</p>
        </div>
        <div className="bg-gray-800 rounded-xl p-4">
          <p className="text-3xl font-semibold text-red-400">{summary.out_of_stock_count || 0}</p>
          <p className="text-sm text-gray-400">Agotados</p>
        </div>
      </div>

      {/* Alerts */}
      {(alerts.low_stock.length > 0 || alerts.out_of_stock.length > 0) && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4">
          <div className="flex items-center gap-2 mb-3">
            <AlertTriangle className="text-red-400" size={20} />
            <h3 className="text-lg font-medium text-red-400">Alertas de Stock</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {alerts.out_of_stock.length > 0 && (
              <div>
                <p className="text-sm text-red-400 font-medium mb-2">Agotados ({alerts.out_of_stock.length})</p>
                <div className="space-y-1">
                  {alerts.out_of_stock.slice(0, 5).map(p => (
                    <div key={p.id} className="text-sm text-gray-300">{p.product_code} - {p.name}</div>
                  ))}
                </div>
              </div>
            )}
            {alerts.low_stock.length > 0 && (
              <div>
                <p className="text-sm text-amber-400 font-medium mb-2">Stock Bajo ({alerts.low_stock.length})</p>
                <div className="space-y-1">
                  {alerts.low_stock.slice(0, 5).map(p => (
                    <div key={p.id} className="text-sm text-gray-300">{p.product_code} - {p.name} ({p.stock} uds)</div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="bg-gray-800 rounded-xl p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar producto..." className="w-full bg-gray-700 border border-gray-600 rounded-lg py-2.5 pl-10 pr-4 text-white placeholder-gray-400 focus:outline-none focus:border-amber-500" />
          </div>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white">
            <option value="stock">Ordenar por Stock</option>
            <option value="name">Ordenar por Nombre</option>
            <option value="product_code">Ordenar por Código</option>
          </select>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="bg-gray-700 border border-gray-600 rounded-lg px-4 py-2.5 text-white">
            <option value="asc">Ascendente</option>
            <option value="desc">Descendente</option>
          </select>
        </div>
      </div>

      {/* Inventory Table */}
      <div className="bg-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-700/50">
              <tr>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Producto</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-300">Código</th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-300">
                  <div className="flex flex-col items-center">
                    <span className="text-amber-400">100ml</span>
                    <span className="text-xs text-gray-500">Stock / Agregar</span>
                  </div>
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-300">
                  <div className="flex flex-col items-center">
                    <span className="text-neon-cyan">35ml</span>
                    <span className="text-xs text-gray-500">Stock / Agregar</span>
                  </div>
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-300">
                  <div className="flex flex-col items-center">
                    <span className="text-pink-400">10ml</span>
                    <span className="text-xs text-gray-500">Regalo promo (valor exacto)</span>
                  </div>
                </th>
                <th className="text-center px-4 py-3 text-sm font-medium text-gray-300">Nuevo Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {loading ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-amber-500 mx-auto"></div></td></tr>
              ) : products.length === 0 ? (
                <tr><td colSpan="6" className="px-4 py-12 text-center text-gray-400"><Package className="mx-auto mb-3" size={40} /><p>No hay productos</p></td></tr>
              ) : products.filter(p => !search || p.name.toLowerCase().includes(search.toLowerCase()) || p.product_code.toLowerCase().includes(search.toLowerCase())).map(product => {
                const changes = pendingChanges[product.id] || {};
                const pending100 = changes.stock_100 || 0;
                const pending40 = changes.stock_40 || 0;
                const stock100 = product.stock || 0;
                const stock40 = product.stock_40ml || 0;
                const newTotal100 = stock100 + pending100;
                const newTotal40 = stock40 + pending40;
                const hasChanges = pending100 !== 0 || pending40 !== 0;
                
                return (
                  <tr key={product.id} className={`hover:bg-gray-700/30 ${hasChanges ? 'bg-amber-500/5' : ''}`}>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <img src={product.main_image || 'https://via.placeholder.com/40'} alt="" className="w-10 h-10 object-cover rounded-lg" />
                        <div>
                          <span className="text-sm text-white truncate block max-w-[180px]">{product.name}</span>
                          <span className="text-xs text-gray-500">{product.inspired_by_brand}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><span className="text-sm text-gray-300 font-mono">{product.product_code}</span></td>
                    
                    {/* 100ml Column */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center gap-2">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-sm font-medium ${getStockColor(stock100)}`}>
                          {stock100}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStockChange(product.id, stock100, stock40, -1, 100)}
                            className="p-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded transition-colors"
                            disabled={stock100 + pending100 <= 0}
                          >
                            <Minus size={12} />
                          </button>
                          <input
                            type="number"
                            value={pending100 || ''}
                            onChange={(e) => handleDirectInput(product.id, stock100, stock40, e.target.value, 100)}
                            placeholder="0"
                            className={`w-12 bg-gray-700 border rounded py-1 px-1 text-white text-center text-xs focus:outline-none ${
                              pending100 !== 0 ? 'border-amber-500' : 'border-gray-600'
                            }`}
                          />
                          <button
                            onClick={() => handleStockChange(product.id, stock100, stock40, 1, 100)}
                            className="p-1 bg-green-500/20 hover:bg-green-500/40 text-green-400 rounded transition-colors"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </td>
                    
                    {/* 40ml Column */}
                    <td className="px-4 py-3">
                      <div className="flex flex-col items-center gap-2">
                        <span className={`inline-flex px-2.5 py-1 rounded-full text-sm font-medium ${getStockColor(stock40)}`}>
                          {stock40}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => handleStockChange(product.id, stock100, stock40, -1, 40)}
                            className="p-1 bg-red-500/20 hover:bg-red-500/40 text-red-400 rounded transition-colors"
                            disabled={stock40 + pending40 <= 0}
                          >
                            <Minus size={12} />
                          </button>
                          <input
                            type="number"
                            value={pending40 || ''}
                            onChange={(e) => handleDirectInput(product.id, stock100, stock40, e.target.value, 40)}
                            placeholder="0"
                            className={`w-12 bg-gray-700 border rounded py-1 px-1 text-white text-center text-xs focus:outline-none ${
                              pending40 !== 0 ? 'border-neon-cyan' : 'border-gray-600'
                            }`}
                          />
                          <button
                            onClick={() => handleStockChange(product.id, stock100, stock40, 1, 40)}
                            className="p-1 bg-green-500/20 hover:bg-green-500/40 text-green-400 rounded transition-colors"
                          >
                            <Plus size={12} />
                          </button>
                        </div>
                      </div>
                    </td>
                    
                    {/* 10ml promo-only stock */}
                    <td className="px-4 py-3">
                      <div className="flex justify-center">
                        <Mini10Cell product={product} fetchWithAuth={fetchWithAuth} onSaved={loadInventory} />
                      </div>
                    </td>

                    {/* New Total Column */}
                    <td className="px-4 py-3">
                      {hasChanges && (
                        <div className="flex flex-col items-center gap-1">
                          {pending100 !== 0 && (
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStockColor(newTotal100)}`}>
                              100ml: {newTotal100}
                              <span className={`ml-1 ${pending100 > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                ({pending100 > 0 ? '+' : ''}{pending100})
                              </span>
                            </span>
                          )}
                          {pending40 !== 0 && (
                            <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getStockColor(newTotal40)}`}>
                              35ml: {newTotal40}
                              <span className={`ml-1 ${pending40 > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                ({pending40 > 0 ? '+' : ''}{pending40})
                              </span>
                            </span>
                          )}
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-700">
            <p className="text-sm text-gray-400">Página {pagination.page} de {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={pagination.page === 1} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Anterior</button>
              <button onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={pagination.page === pagination.pages} className="px-3 py-1.5 bg-gray-700 text-white rounded-lg disabled:opacity-50">Siguiente</button>
            </div>
          </div>
        )}
      </div>

      {/* Instructions */}
      <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700">
        <h4 className="text-sm font-medium text-white mb-2">¿Cómo funciona?</h4>
        <ul className="text-sm text-gray-400 space-y-1">
          <li>• Cada producto tiene dos presentaciones: <span className="text-amber-400">100ml</span> y <span className="text-neon-cyan">35ml</span></li>
          <li>• El stock se gestiona de forma <strong>independiente</strong> para cada presentación</li>
          <li>• Usa los botones <span className="text-green-400">+</span> / <span className="text-red-400">-</span> o escribe directamente la cantidad a agregar/quitar</li>
          <li>• Los cambios se muestran en amarillo hasta que presiones <strong>"Guardar"</strong></li>
          <li>• El precio de 100ml varía según el tipo de perfume, el de 40ml es fijo ($200)</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminInventory;
